import React, { useCallback, useEffect, useState, useContext } from 'react';

import { ArrowBackRounded, SettingsBackupRestoreRounded } from '@mui/icons-material';
import { Box, Button, IconButton } from '@mui/material';
import {
  UserChipField,
  OrdDatagridSingleRowSelection,
  OrdCreateDialog,
  OrdList,
  FiqlSearch,
  ListTableSizeContext,
  OrdDatagridHeaderWithHints,
  OrdIconStandard,
  useCanDelete,
  useCanEdit,
} from '@ragu-components/ord-components';
import { Resources } from '@ragu-components/ord-resources';
import { MobilityConfigurationReadFields, dataSchema } from '@ragu-components/ord-types';
import { useReactFlow } from '@xyflow/react';
import {
  TextField,
  DeleteWithConfirmButton,
  useTranslate,
  useRecordContext,
  ArrayField,
  ChipField,
  SingleFieldList,
  FunctionField,
  useListContext,
  usePermissions,
} from 'react-admin';
import { flushSync } from 'react-dom';
import { useNavigate } from 'react-router-dom';

import { flowchartLogger } from 'src/lib/logger';

import { personalEndpointContext } from './context/personalEndpointContext';
import FrontendData from './dataProvider/prepareFrontendData';
import useBackendData from './dataProvider/useBackendData';
import { useIngordDataProvider } from './hooks/useIngordDataProvider';

import type { RaRecord } from 'react-admin';

interface FlowchartResourceListBaseProps {
  /** Resource name. */
  resource: string;
  /** Optional initial data for the list. */
  initialData: RaRecord[] | undefined;
  /** Sets the active record context. */
  setRecord: (value: RaRecord | undefined) => void;
  /** Whether the current record has unsaved changes. */
  recordChanged: boolean;
  /** Sets the recordChanged flag. */
  setRecordChanged: (value: boolean) => void;
  /** Bypasses navigation prompt when switching records. */
  setIgnoreNavigationPrompt: (value: boolean) => void;
}

interface FlowchartResourceListTableProps extends FlowchartResourceListBaseProps {
  flowchartRecord: RaRecord | undefined;
  isPersonal: boolean;
  showBackupsButton: boolean;
  onShowBackups: (record: RaRecord | undefined) => void;
}

interface FlowchartResourceListProps extends FlowchartResourceListBaseProps {
  /** Indicates whether the list is visible - controlled outside of the component. */
  listVisible: boolean;
  /** Edit form to render. */
  editForm: React.ReactNode;
  /** Backup version field name. */
  backupVersionField?: string;
}

interface ShowBackupsBtnProps {
  onBtnClick: (record: RaRecord | undefined) => void;
}

const ShowBackupsBtn = ({ onBtnClick }: ShowBackupsBtnProps): React.ReactElement => {
  const record = useRecordContext();
  const translate = useTranslate();
  const { listTableSize } = React.useContext(ListTableSizeContext);

  return (
    <IconButton
      onClick={(event) => {
        event.stopPropagation();
        onBtnClick(record);
      }}
      title={translate('callflows.interface.showBackups')}
      sx={listTableSize === 'small' ? { padding: 0 } : undefined}
    >
      <SettingsBackupRestoreRounded />
    </IconButton>
  );
};

const RecordCreateForm = ({
  resource,
  children,
}: {
  resource: string;
  children: React.ReactNode;
}): React.ReactElement => {
  const backendData = useBackendData();
  const navigate = useNavigate();
  const isPersonal = useContext(personalEndpointContext);
  const redirectBase = isPersonal ? `me/${resource}` : resource;
  const createEmptyRecord = useCallback(() => backendData({ nodes: [], edges: [] }, resource), [backendData, resource]);
  const [data, setData] = useState(() => createEmptyRecord());

  useEffect(() => {
    setData(createEmptyRecord());
  }, [createEmptyRecord]);

  return data !== false ? (
    <OrdCreateDialog
      fullWidth
      slotProps={{ paper: { sx: { width: '700px', maxWidth: '700px' } } }}
      record={data}
      mutationOptions={{
        // Redirect to the newly created record
        onSuccess: (data: RaRecord) => {
          setData(createEmptyRecord());
          void navigate(`/${redirectBase}/${data.id}`);
        },
      }}
    >
      {children}
    </OrdCreateDialog>
  ) : (
    <></>
  );
};

const FlowchartResourceListTable = ({
  resource,
  initialData,
  flowchartRecord,
  recordChanged,
  setRecord,
  setRecordChanged,
  setIgnoreNavigationPrompt,
  isPersonal,
  showBackupsButton,
  onShowBackups,
}: FlowchartResourceListTableProps): React.ReactElement => {
  const reactFlowInstance = useReactFlow();
  const translate = useTranslate();
  const navigate = useNavigate();
  const { data: listData } = useListContext();
  const { isPending: permissionsPending } = usePermissions();
  const canEdit = useCanEdit();
  const canDelete = useCanDelete();

  const handleToggleItem = (id: RaRecord['id']): void => {
    const listRecords = (listData ?? []) as RaRecord[];
    const record = initialData?.find((item) => item.id === id) ?? listRecords.find((item) => item.id === id);

    if (record === undefined) {
      return;
    }

    flowchartLogger.debug('Clicked on flowchart list row', id, resource, record);
    if (recordChanged) {
      const shouldContinue = window.confirm(translate('callflows.interface.unsavedExit'));
      if (!shouldContinue) {
        return;
      }
      flushSync(() => {
        setIgnoreNavigationPrompt(true);
        setRecordChanged(false);
      });
    }
    if (record.id === flowchartRecord?.id) {
      return;
    }
    if ('entry' in record && 'nodes' in record) {
      const frontendData = new FrontendData({ entry: record.entry, nodes: record.nodes });
      const { nodes, edges } = frontendData.prepare();
      setRecord(record);
      reactFlowInstance.setNodes(nodes);
      reactFlowInstance.setEdges(edges);
      setTimeout(() => {
        void reactFlowInstance.fitView();
      }, 0);
    }
    // While permissions load, canEdit sees no permissions and would misroute editors to the
    // readonly view; go to the edit route instead — FlowchartReadonlyRedirect re-evaluates
    // reactively once permissions resolve and sends readonly users to /show.
    const canEditRecord = isPersonal || permissionsPending || canEdit(resource, record);
    void navigate(canEditRecord ? `/${resource}/${id}` : `/${resource}/${id}/show`);
  };

  return (
    <OrdDatagridSingleRowSelection
      resource={resource}
      selectedId={flowchartRecord?.id}
      onToggleItem={handleToggleItem}
      sx={{
        '& .RaDatagrid-tableWrapper': {
          height: 'calc(100vh - 96px)',
          maxHeight: 'calc(100vh - 96px)',
        },
      }}
      data={initialData}
      header={OrdDatagridHeaderWithHints}
      isRowSelectable={() => true}
    >
      <TextField source='name' />
      {resource === Resources.callflows && <TextField source='priority' />}
      {resource === Resources.callflows && (
        <ArrayField source='assigned_inbound_e164s' sortable={false}>
          <SingleFieldList linkType={false}>
            <ChipField source='e164_match' />
          </SingleFieldList>
        </ArrayField>
      )}
      <TextField source='description' />
      {resource === Resources.mobility_configurations && !isPersonal && (
        <UserChipField sortable={false} source={MobilityConfigurationReadFields.owner} />
      )}
      {showBackupsButton && <ShowBackupsBtn onBtnClick={onShowBackups} />}
      <FunctionField
        render={(record: RaRecord) =>
          isPersonal || canDelete(resource, record) ? (
            <DeleteWithConfirmButton
              label=''
              sx={{ minWidth: 'auto', '& .MuiButton-startIcon': { margin: 0 } }}
              confirmContent={translate('callflows.notifications.deleteConfirmation')}
              confirmTitle={translate('callflows.notifications.deleteConfirmationTitle', { title: record.name })}
            />
          ) : null
        }
      />
    </OrdDatagridSingleRowSelection>
  );
};
const FlowchartResourceList = ({
  resource,
  listVisible,
  setRecord,
  initialData,
  editForm,
  backupVersionField = undefined,
  recordChanged,
  setRecordChanged,
  setIgnoreNavigationPrompt,
}: FlowchartResourceListProps): React.ReactElement => {
  const reactFlowInstance = useReactFlow();
  const flowchartRecord = useRecordContext();
  const dataProvider = useIngordDataProvider();
  const translate = useTranslate();
  const isPersonal = useContext(personalEndpointContext);
  const [backupsParentRecord, setBackupsParentRecord] = useState<RaRecord | undefined>(undefined);
  const schema =
    resource === Resources.mobility_configurations
      ? isPersonal
        ? dataSchema.PersonalMobilityConfigurationRead.title
        : dataSchema.MobilityConfigurationRead.title
      : dataSchema.CallflowRead.title;

  useEffect(() => {
    const fetchRecord = async (): Promise<void> => {
      if (
        flowchartRecord?.is_backup === true &&
        backupVersionField !== undefined &&
        flowchartRecord[backupVersionField] !== undefined
      ) {
        dataProvider
          .getOne<RaRecord>(resource, { id: flowchartRecord[backupVersionField] })
          .then((result) => {
            const record = result.data;
            setBackupsParentRecord(record);
          })
          .catch((error) => {
            flowchartLogger.error('Failed to fetch record parent', { error });
            setBackupsParentRecord(undefined);
          });
      }
    };
    void fetchRecord();
  }, [dataProvider, flowchartRecord, setBackupsParentRecord, resource, backupVersionField]);

  // fit view on list visibility change
  useEffect(() => {
    if (!listVisible) {
      void reactFlowInstance.fitView({ maxZoom: 1, duration: 500, padding: 0.1 });
    }
  }, [listVisible, reactFlowInstance]);

  return (
    <>
      <Box
        sx={{
          height: 'auto',
          display: 'flex',
          minHeight: 0,
          flexDirection: 'column',
          '& .list-page': { pt: 0, mb: 0 },
        }}
        data-testid='dialplan-list'
      >
        {backupsParentRecord !== undefined && (
          <Button
            onClick={() => {
              setBackupsParentRecord(undefined);
            }}
            color='error'
            sx={{ gap: 1 }}
          >
            <ArrowBackRounded />
            {translate('callflows.interface.backToMainList')}
          </Button>
        )}
        <OrdList
          showTabTitle={false}
          filter={{
            active: 'fiql',
            fiql:
              backupVersionField !== undefined
                ? `(${backupVersionField}=${
                    backupsParentRecord !== undefined ? `=${backupsParentRecord.id}` : 'isnull=true'
                  })`
                : undefined,
          }}
          title={
            backupsParentRecord !== undefined
              ? translate('callflows.interface.backupsForTitle', { title: backupsParentRecord.name })
              : translate(`resources.${resource}.name`)
          }
          icon={
            <OrdIconStandard
              name={resource === Resources.mobility_configurations ? 'mobilityConfigurations' : 'callflows'}
            />
          }
          // TODO: update sorting for backups view
          sort={{ field: 'name', order: 'ASC' }}
          headerChildren={<FiqlSearch schema={schema} />}
        >
          <FlowchartResourceListTable
            resource={resource}
            initialData={initialData}
            flowchartRecord={flowchartRecord}
            recordChanged={recordChanged}
            setRecord={setRecord}
            setRecordChanged={setRecordChanged}
            setIgnoreNavigationPrompt={setIgnoreNavigationPrompt}
            isPersonal={isPersonal}
            showBackupsButton={backupsParentRecord === undefined}
            onShowBackups={setBackupsParentRecord}
          />
        </OrdList>
        <RecordCreateForm resource={resource}>{editForm}</RecordCreateForm>
      </Box>
    </>
  );
};

export default FlowchartResourceList;
